import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  BarChart3,
  Clock3,
  ExternalLink,
  MousePointerClick,
  Search,
} from "lucide-react";

import axiosClient from "../../api/axiosClient";
import WorkspaceTitleHeader from "../../components/common/WorkspaceTitleHeader";

const SUCCESS_STATUSES = new Set([
  "sent",
  "delivered",
  "dispatched",
  "completed",
]);
const LAST_ANALYTICS_KEY = "xb_last_campaign_analytics_id";

function cx(...values) {
  return values.filter(Boolean).join(" ");
}

function normalizeStatusValue(status) {
  return String(status || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

function isReportReady(campaign) {
  const status = normalizeStatusValue(campaign?.status);
  return Boolean(campaign?.sentAt) || SUCCESS_STATUSES.has(status);
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function getStatusTone(status) {
  const normalized = normalizeStatusValue(status);
  if (SUCCESS_STATUSES.has(normalized)) {
    return "bg-emerald-50 text-emerald-700 border-emerald-200";
  }
  if (normalized === "scheduled" || normalized === "queued") {
    return "bg-amber-50 text-amber-700 border-amber-200";
  }
  if (normalized === "failed" || normalized === "error") {
    return "bg-rose-50 text-rose-700 border-rose-200";
  }
  return "bg-slate-100 text-slate-700 border-slate-200";
}

function sortByMostRecent(items) {
  return [...items].sort((a, b) => {
    const aTime = new Date(a.sentAt || a.updatedAt || a.createdAt || 0).getTime();
    const bTime = new Date(b.sentAt || b.updatedAt || b.createdAt || 0).getTime();
    return bTime - aTime;
  });
}

export default function CampaignReportsSelectorPage() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [selectedCampaignId, setSelectedCampaignId] = useState("");

  useEffect(() => {
    let active = true;

    async function loadCampaigns() {
      setLoading(true);
      setError("");

      try {
        const res = await axiosClient.get("/campaign/get-image-campaign");
        const items = Array.isArray(res.data) ? res.data : res.data?.items || [];
        const reportableCampaigns = sortByMostRecent(items.filter(isReportReady));
        const savedCampaignId = localStorage.getItem(LAST_ANALYTICS_KEY) || "";

        if (!active) return;

        setCampaigns(reportableCampaigns);
        setSelectedCampaignId(prev => {
          if (prev && reportableCampaigns.some(item => item.id === prev)) return prev;
          if (
            savedCampaignId &&
            reportableCampaigns.some(item => item.id === savedCampaignId)
          ) {
            return savedCampaignId;
          }
          return reportableCampaigns[0]?.id || "";
        });
      } catch (err) {
        if (!active) return;
        setCampaigns([]);
        setError(
          err?.response?.status === 403
            ? "Campaign listing is not available for this account right now."
            : "Unable to load campaigns right now."
        );
      } finally {
        if (active) setLoading(false);
      }
    }

    loadCampaigns();

    return () => {
      active = false;
    };
  }, []);

  const filteredCampaigns = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return campaigns;

    return campaigns.filter(item => {
      const haystack = [
        item?.name,
        item?.status,
        item?.id,
        item?.templateName,
        item?.channel,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [campaigns, search]);

  const selectedCampaign = useMemo(
    () => campaigns.find(item => item.id === selectedCampaignId) || null,
    [campaigns, selectedCampaignId]
  );

  const openReport = campaignId => {
    const value = String(campaignId || "").trim();
    if (!value) return;
    localStorage.setItem(LAST_ANALYTICS_KEY, value);
    navigate(`/app/campaigns/${value}/reports/logs?tab=recipients&windowDays=90`);
  };

  const openClickActivity = campaignId => {
    const value = String(campaignId || "").trim();
    if (!value) return;
    localStorage.setItem(LAST_ANALYTICS_KEY, value);
    navigate(`/app/campaigns/logs/${value}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-slate-50 to-slate-100 px-8 pb-6 pt-6 font-sans text-[14px] sm:px-10">
      <div className="mx-auto flex max-w-[1600px] flex-col gap-4">
        <WorkspaceTitleHeader
          icon={BarChart3}
          title="Campaign Analytics"
          subtitle="Select a sent campaign to open its report or click activity."
          actions={
            <button
              type="button"
              onClick={() => navigate("/app/campaigns/template-campaigns-list")}
              className="inline-flex items-center gap-2 rounded-[10px] border border-slate-200 bg-white px-4 py-2 text-xs font-medium text-slate-700 transition hover:border-slate-300 hover:text-slate-900"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Campaigns
            </button>
          }
        />

        <section className="overflow-hidden rounded-[10px] border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 bg-[linear-gradient(180deg,rgba(16,185,129,0.08)_0%,rgba(248,250,252,0.92)_100%)] px-5 py-4">
            <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
              <div>
                <p className="mt-1 text-xs text-slate-500">
                  Select a sent campaign to open its report or click activity.
                </p>
              </div>
              <div className="inline-flex items-center whitespace-nowrap rounded-[10px] border border-emerald-200 bg-white px-3 py-1.5 text-xs font-medium text-emerald-700">
                {campaigns.length} campaigns
              </div>
              <div className="grid w-full gap-3 xl:max-w-[1120px] xl:grid-cols-[minmax(0,1fr)_280px_auto_auto]">
                <label className="relative block w-full">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={search}
                    onChange={event => setSearch(event.target.value)}
                    placeholder="Search by campaign name, status, or ID"
                    className="h-10 w-full rounded-[10px] border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                  />
                </label>
                <select
                  value={selectedCampaignId}
                  onChange={event => setSelectedCampaignId(event.target.value)}
                  className="h-10 w-full rounded-[10px] border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                >
                  <option value="">Select campaign</option>
                  {campaigns.map(item => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => openReport(selectedCampaign?.id)}
                  disabled={!selectedCampaign}
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-[10px] bg-slate-900 px-4 text-xs font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <BarChart3 className="h-4 w-4" />
                  Open Report
                </button>
                <button
                  type="button"
                  onClick={() => openClickActivity(selectedCampaign?.id)}
                  disabled={!selectedCampaign}
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-[10px] border border-slate-200 bg-white px-4 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <MousePointerClick className="h-4 w-4" />
                  Open Click Activity
                </button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="px-5 py-10 text-sm text-slate-500">
              Loading sent campaigns...
            </div>
          ) : error ? (
            <div className="px-5 py-6">
              <div className="rounded-[10px] border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                {error}
              </div>
            </div>
          ) : filteredCampaigns.length ? (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50/80 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                    <tr>
                      <th className="px-5 py-3">Campaign</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Recipients</th>
                      <th className="px-4 py-3">Sent At</th>
                      <th className="px-4 py-3">Updated</th>
                      <th className="px-5 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {filteredCampaigns.map(item => {
                      const isActive = item.id === selectedCampaignId;
                      return (
                        <tr
                          key={item.id}
                          className={cx(
                            "transition hover:bg-slate-50",
                            isActive && "bg-emerald-50/40"
                          )}
                        >
                          <td className="px-5 py-3.5">
                            <button
                              type="button"
                              onClick={() => setSelectedCampaignId(item.id)}
                              className="text-left"
                            >
                              <div className="font-medium text-slate-900">
                                {item.name}
                              </div>
                              <div className="mt-1 text-xs text-slate-500">
                                {item.id}
                              </div>
                            </button>
                          </td>
                          <td className="px-4 py-3.5">
                            <span
                              className={cx(
                                "inline-flex items-center rounded-[10px] border px-2.5 py-1 text-xs font-medium capitalize",
                                getStatusTone(item.status)
                              )}
                            >
                              {item.status || "Unknown"}
                            </span>
                          </td>
                          <td className="px-4 py-3.5 text-slate-600">
                            {item.recipients || 0}
                          </td>
                          <td className="px-4 py-3.5 text-slate-600">
                            {formatDateTime(item.sentAt)}
                          </td>
                          <td className="px-4 py-3.5 text-slate-600">
                            <div className="inline-flex items-center gap-1.5">
                              <Clock3 className="h-3.5 w-3.5 text-slate-400" />
                              {formatDateTime(item.updatedAt || item.sentAt)}
                            </div>
                          </td>
                          <td className="px-5 py-3.5 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => openReport(item.id)}
                                className="inline-flex items-center gap-2 rounded-[10px] border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 transition hover:border-slate-300 hover:text-slate-900"
                              >
                                <ExternalLink className="h-4 w-4" />
                                Report
                              </button>
                              <button
                                type="button"
                                onClick={() => openClickActivity(item.id)}
                                className="inline-flex items-center gap-2 rounded-[10px] border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 transition hover:border-slate-300 hover:text-slate-900"
                              >
                                <MousePointerClick className="h-4 w-4" />
                                Click Activity
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="border-t border-slate-200 bg-slate-50/60 px-5 py-3 text-xs text-slate-600">
                Showing {filteredCampaigns.length} of {campaigns.length} reportable
                campaigns
              </div>
            </>
          ) : (
            <div className="px-5 py-10 text-sm text-slate-500">
              No sent campaigns matched your search.
            </div>
          )}
        </section>

      </div>
    </div>
  );
}
